import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { twcn } from '@web/ui/core/utils';

/*
 * Input — VOLL DS TextField.
 *
 * Sizes (matched to the row-height token scale used elsewhere):
 *   - sm:      h-row-h (32 px)         — toolbar filters, dense forms
 *   - default: h-10 (40 px)             — standard form input
 *   - lg:      h-12 (48 px)             — prominent / landing forms
 *
 * The component does not accept a `className` prop. Visual decisions
 * live in the variant table here. To add a leading glyph use the
 * `leadingIcon` slot; to add a fixed prefix block use `startAdornment`.
 *
 * Extensions kept until DS catches up:
 *   - focus-within:border-primary + ring shadow
 *   - aria-[invalid=true]:border-destructive
 *   - disabled / read-only: bg-accent + text-muted-foreground
 */
const inputVariants = cva(
  'flex w-full items-center gap-2 overflow-hidden rounded-md border border-input bg-card text-meta text-foreground ' +
    'transition-[border-color,box-shadow] duration-base ease-standard ' +
    'focus-within:border-primary focus-within:shadow-ring-primary-lg ' +
    '[&:has([aria-invalid=true])]:border-destructive [&:has([aria-invalid=true]):focus-within]:shadow-ring-destructive',
  {
    variants: {
      size: {
        sm: 'h-row-h px-2.5',
        default: 'h-10 px-3',
        lg: 'h-12 px-3.5 text-base',
      },
      inert: {
        true: 'cursor-not-allowed bg-accent text-muted-foreground',
        false: '',
      },
      hasStartAdornment: {
        true: 'pl-0',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      inert: false,
      hasStartAdornment: false,
    },
  },
);

type InputSize = NonNullable<VariantProps<typeof inputVariants>['size']>;

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> & {
  /** Visual size variant — matches the row-height tokens. */
  size?: InputSize;
  /** Icon rendered before the input with a small gap. */
  leadingIcon?: ReactNode;
  /** Fixed prefix block with a right divider (e.g. `PROJECT.`). */
  startAdornment?: ReactNode;
  /** Render the input text in the mono font (codes, identifiers). */
  mono?: boolean;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size, type = 'text', leadingIcon, startAdornment, disabled, readOnly, mono, ...props },
  ref,
) {
  const inert = disabled || readOnly;
  const container = inputVariants({ size, inert, hasStartAdornment: !!startAdornment });

  return (
    <div className={container}>
      {startAdornment ? (
        <span className="h-full inline-flex items-center whitespace-nowrap border-r border-input bg-accent px-2.5 font-mono text-meta text-muted-foreground">
          {startAdornment}
        </span>
      ) : null}
      {leadingIcon ? (
        <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-muted-foreground">
          {leadingIcon}
        </span>
      ) : null}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        className={twcn(
          'h-full flex-1 bg-transparent outline-none placeholder:text-faint-foreground',
          inert ? 'cursor-not-allowed text-muted-foreground' : 'text-foreground',
          mono && 'font-mono',
          startAdornment && 'px-3',
        )}
        {...props}
      />
    </div>
  );
});
